// WHAT: Legend component explaining the Impact vs Effort Matrix quadrants
// WHY:  Provides visual explanation of matrix categories for user understanding
// DOMAIN: Admin → Roadmap → Matrix
// DEPENDENCIES: Card, Badge components, Lucide icons
// RISKS:
//   - NOTE: Dynamic Tailwind classes may not work with JIT compiler
//   - NOTE: Uses index as key in map - consider using unique identifier

import { LucideBarChart3 } from "lucide-react";
import React from "react";

import { Card, Badge } from "@/components/ui";

/**
 * WHAT:
 *   Component that displays a legend explaining the four quadrants of the
 *   Impact vs Effort Matrix (Quick Wins, Major Projects, Fill-ins, Thankless Tasks).
 *
 * WHY:
 *   Helps users understand the matrix categorization and prioritize items
 *   based on impact and effort scores.
 *
 * BEHAVIOR:
 *   - Displays four legend items in responsive grid (1/2 columns)
 *   - Each item shows color indicator, title badge, and description
 *   - Uses quadrant-specific colors and variants
 *
 * RISKS:
 *   - NOTE: Dynamic Tailwind classes may not be purged correctly - use static classes
 *   - NOTE: Index used as key - should use unique identifier if legend items change
 *
 * @returns {JSX.Element} Die gerenderte MatrixLegend-Komponente
 *
 */
export const MatrixLegend: React.FC = () => {
  const legendItems = [
    {
      color: "green-500",
      description: "Implement immediately",
      title: "Quick Wins",
      variant: "success" as const,
    },
    {
      color: "tracq-bright-blue",
      description: "Plan & allocate resources",
      title: "Major Projects",
      variant: "primary" as const,
    },
    {
      color: "slate-500",
      description: "Implement when time allows",
      title: "Fill-ins",
      variant: "default" as const,
    },
    {
      color: "orange-500",
      description: "Avoid if possible",
      title: "Thankless Tasks",
      variant: "warning" as const,
    },
  ];

  return (
    <Card padding="lg" variant="default">
      <div className="flex items-center gap-2 mb-6">
        <LucideBarChart3 className="w-5 h-5 text-tracq-bright-blue" />
        <h3 className="text-lg font-medium text-white">Matrix Legend</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          {legendItems.slice(0, 2).map((item) => (
            <Card key={item.title} padding="md" variant="outlined">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 bg-${item.color} rounded-full shadow-lg`} />
                <div>
                  <Badge className="mb-1" size="sm" style="subtle" variant={item.variant}>
                    {item.title}
                  </Badge>
                  <p className="text-slate-500 text-xs">{item.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="space-y-4">
          {legendItems.slice(2, 4).map((item) => (
            <Card key={item.title} padding="md" variant="outlined">
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 bg-${item.color} rounded-full shadow-lg`} />
                <div>
                  <Badge className="mb-1" size="sm" style="subtle" variant={item.variant}>
                    {item.title}
                  </Badge>
                  <p className="text-slate-500 text-xs">{item.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Card>
  );
};
